import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Flow, FlowRun, FlowRunEvent } from '@/lib/api';
import { UUID_REGEX, updateDocument } from '@/lib/api';
import { flowPosture } from '@/lib/api/flows';
import type { DocumentRendererProps } from '@/lib/document-renderer-registry';
import {
  useFlowActions,
  useFlowRunEvents,
  useFlowRuns,
  useFlows,
} from '@/hooks/use-flows';
import { formatTriggerSummary } from '@/components/features/flows-manager';
import { formatNextCron } from '@/lib/cron-label';
import { RunStatusBadge } from '@/components/features/flow-run-history';
import {
  collectManualTriggerPayloadInteractive,
  needsManualTriggerPayload,
} from '@/components/features/flows/manual-trigger-payload';
import { FlowDefinitionPipeline } from './flow-definition-pipeline';
import {
  FLOW_OPEN_TAB_EVENT,
  FLOW_RUN_NOW_EVENT,
  readFlowTab,
  rememberFlowTab,
  type FlowDocTab,
} from './flow-studio-events';

function normalizeFlowFilePath(path: string | null | undefined) {
  if (!path || typeof path !== 'string') return '';
  return path.replace(/^\/+/u, '').trim();
}
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import {
  Loader2,
  Play,
  Eye,
  Code,
  AlignCenter,
  StretchHorizontal,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  Sparkles,
  AlertTriangle,
  Inbox,
  GitBranch,
  FileText,
  Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import { openBasecampAuthWindow } from '@/lib/basecamp-api';
import { useBasecampAuth } from '@/hooks/use-basecamp';

// ---------------------------------------------------------------------------
// Error action map
// Maps known run error strings to a contextual settings action.
// ---------------------------------------------------------------------------
interface ErrorAction { label: string; settingsTab?: string }

const ERROR_ACTION_MAP: Array<{ pattern: RegExp } & ErrorAction> = [
  { pattern: /no gitsource matches/i,          label: 'Configure Git Sources', settingsTab: 'git-sources' },
  { pattern: /git.source.*not found/i,         label: 'Configure Git Sources', settingsTab: 'git-sources' },
  { pattern: /requirements not met.*git_source/i, label: 'Configure Git Sources', settingsTab: 'git-sources' },
  { pattern: /basecamp.*not configured/i,      label: 'Configure Basecamp',    settingsTab: 'basecamp' },
  { pattern: /requirements not met.*basecamp/i, label: 'Configure Basecamp',   settingsTab: 'basecamp' },
];

const BASECAMP_AUTH_ERROR =
  /connect basecamp|re-authoriz|not connected for your account|token expired or refresh failed/i;

function flowNeedsBasecampAuth(fm: Record<string, unknown>, latestError?: string | null): boolean {
  const requires = Array.isArray(fm.requires) ? fm.requires.map(String) : [];
  if (requires.includes('basecamp')) return true;
  if (BASECAMP_AUTH_ERROR.test(String(latestError || ''))) return true;
  const steps = Array.isArray(fm.steps) ? fm.steps : [];
  return steps.some((step) => {
    if (!step || typeof step !== 'object') return false;
    const id = String((step as { id?: unknown }).id || '');
    return id.startsWith('basecamp.') || id.startsWith('late_todos.');
  });
}

function getErrorAction(error: string | null | undefined): ErrorAction | null {
  if (!error) return null;
  return ERROR_ACTION_MAP.find(({ pattern }) => pattern.test(error)) ?? null;
}

function openSettings(tab: string) {
  window.dispatchEvent(new CustomEvent('open-settings', { detail: { tab } }));
}

function runErrorAction(action: ErrorAction) {
  if (action.settingsTab) openSettings(action.settingsTab);
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diffMs = now - time;
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
}

function coerceTriggers(fmTriggers: unknown, flowTriggers: Flow['triggers'] | undefined): Flow['triggers'] {
  if (flowTriggers?.length) return flowTriggers;
  if (!fmTriggers || !Array.isArray(fmTriggers)) return [];
  return fmTriggers.filter(
    (t): t is { id: string; config?: Record<string, unknown> } =>
      !!t &&
      typeof t === 'object' &&
      typeof (t as { id?: string }).id === 'string'
  ).map((t) => ({
    id: String(t.id),
    config: (typeof t.config === 'object' && t.config !== null ? t.config : {}) as Record<string, unknown>,
  }));
}

function nextScheduleHint(triggers: Flow['triggers']): string {
  const tr = triggers[0];
  if (!tr) return '-';
  if (tr.id === 'schedule.cron' && tr.config?.expr != null) {
    const expr = String(tr.config.expr);
    const timezone = typeof tr.config.timezone === 'string'
      ? tr.config.timezone
      : 'America/Los_Angeles';
    return formatNextCron(expr, timezone);
  }
  return 'Event-driven';
}

function summarizeStepResult(r: Record<string, unknown>): string | undefined {
  if (typeof r.summary === 'string') return r.summary;
  if (typeof r.message === 'string') return r.message;

  const bits: string[] = [];
  if (r.skipped === true) bits.push('skipped (no matching work spec)');
  if (r.created === true) bits.push('created stub work spec');
  if (r.appended === true) bits.push('appended comment to work spec');
  if (r.created !== true && r.appended !== true && r.skipped !== true && typeof r.document_id === 'string') {
    bits.push('matched existing work spec');
  }
  if (typeof r.file_path === 'string' && r.file_path.trim()) {
    const path = r.file_path.trim();
    const short = path.length > 64 ? `...${path.slice(-61)}` : path;
    bits.push(short);
  }
  if (bits.length) return bits.join(' - ');
  return undefined;
}

function summarizePayload(p: Record<string, unknown>): string | undefined {
  if (!p || typeof p !== 'object') return undefined;
  if (typeof (p as { error?: unknown }).error === 'string')
    return (p as { error: string }).error;
  if (typeof (p as { message?: unknown }).message === 'string')
    return (p as { message: string }).message;
  if (typeof (p as { summary?: unknown }).summary === 'string')
    return (p as { summary: string }).summary;
  const result = (p as { result?: unknown }).result;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return summarizeStepResult(result as Record<string, unknown>);
  }
  return undefined;
}

function openDocumentFromStep(documentId: string) {
  window.dispatchEvent(
    new CustomEvent('open-document', {
      detail: { documentId },
    }),
  );
}

function stepsFromEvents(events: FlowRunEvent[], runStatus?: string | null): Array<{
  label: string;
  status: 'done' | 'skipped' | 'error' | 'running';
  detail?: string;
  result?: Record<string, unknown>;
}> {
  const byStep = new Map<string, {
    label: string;
    status: 'done' | 'skipped' | 'error' | 'running';
    detail?: string;
    result?: Record<string, unknown>;
  }>();
  for (const e of events) {
    if (
      e.event_type !== 'flow.step.started' &&
      e.event_type !== 'flow.step.completed' &&
      e.event_type !== 'flow.step.failed'
    ) {
      continue;
    }
    const label = e.step_id ?? 'step';
    const status =
      e.event_type === 'flow.step.failed'
        ? 'error'
        : e.event_type === 'flow.step.started'
          ? 'running'
          : 'done';
    const prev = byStep.get(label);
    if (prev && (prev.status === 'done' || prev.status === 'error') && status === 'running') {
      continue;
    }
    byStep.set(label, {
      label,
      status,
      detail: summarizePayload(e.payload),
      result:
        e.payload.result && typeof e.payload.result === 'object' && !Array.isArray(e.payload.result)
          ? (e.payload.result as Record<string, unknown>)
          : undefined,
    });
  }
  if (String(runStatus || '').toLowerCase() === 'failed') {
    for (const [label, row] of byStep) {
      if (row.status === 'running') byStep.set(label, { ...row, status: 'error' });
    }
  }
  return [...byStep.values()];
}

function RunStepLog({
  steps,
  selectedIndex,
  onSelect,
}: {
  steps: Array<{ label: string; status: 'done' | 'skipped' | 'error' | 'running'; detail?: string; result?: Record<string, unknown> }>;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const doneCount = steps.filter((s) => s.status === 'done').length;

  if (steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground/95 py-4">
        Waiting for the first step to start...
      </p>
    );
  }

  return (
    <div className="border border-border" data-testid={testIds.flowDocumentRenderer.stepLog}>
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2.5 bg-muted/30 hover:bg-muted/40 text-left',
          expanded && 'border-b border-border'
        )}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground/90 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground/90 shrink-0" />
        )}
        <span className="text-sm font-medium text-foreground flex-1">Execution steps ({steps.length})</span>
        <span className="text-xs tabular-nums text-muted-foreground/95 font-medium">
          {doneCount}/{steps.length} done
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-border">
          {steps.map((step, idx) => (
            <div
              key={`step-${step.label}-${idx}`}
              role="button"
              tabIndex={0}
              aria-pressed={selectedIndex === idx}
              data-testid={testIds.flowDocumentRenderer.runStep(idx)}
              onClick={() => onSelect(idx)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(idx);
                }
              }}
              className={cn(
                'px-3 py-2.5 flex w-full items-start gap-3 text-left',
                selectedIndex === idx ? 'bg-primary/5' : 'hover:bg-muted/40',
              )}
            >
              <span
                className={cn(
                  'mt-1 w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center shrink-0',
                  step.status === 'done' && 'bg-green-500/20 text-green-400',
                  step.status === 'skipped' && 'bg-muted text-muted-foreground',
                  step.status === 'error' && 'bg-red-500/20 text-red-400',
                  step.status === 'running' && 'bg-blue-500/20 text-blue-400'
                )}
              >
                {step.status === 'running' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : step.status === 'done' ? '\u2713' : step.status === 'error' ? '!' : '\u2013'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground leading-snug">{step.label}</p>
                {step.detail && (
                  <p className="text-xs text-muted-foreground/95 mt-1 leading-relaxed">{step.detail}</p>
                )}
                {step.result && typeof step.result.sha === 'string' && (
                  <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-foreground/80">
                      <GitBranch className="h-3 w-3 text-muted-foreground shrink-0" />
                      {typeof step.result.branch === 'string' ? step.result.branch : ''}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground/90">
                      {step.result.sha.slice(0, 8)}
                    </span>
                  </div>
                )}
                {step.result && typeof step.result.document_id === 'string' && (
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1.5"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDocumentFromStep(step.result!.document_id as string);
                      }}
                    >
                      <FileText className="h-3 w-3" />
                      Open work spec
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                    </Button>
                    {step.result.created === true && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal">
                        created
                      </Badge>
                    )}
                    {step.result.appended === true && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal">
                        comment appended
                      </Badge>
                    )}
                    {step.result.skipped === true && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal">
                        skipped
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FlowDocumentRenderer({ document }: DocumentRendererProps) {
  const flowId = document.id;
  const isPersisted = UUID_REGEX.test(flowId);
  const docPath = document.filePath || document.file_path;
  const docPathNormalized = normalizeFlowFilePath(docPath ?? null);

  const { flows, error: flowsError, refetch: refetchFlowsList } =
    useFlows();

  const flow = useMemo(() => {
    if (!flows.length) return null;
    const byId = flows.find((f) => f.id === flowId);
    if (byId) return byId;
    if (docPathNormalized.length > 0) {
      return flows.find((f) => normalizeFlowFilePath(f.file_path) === docPathNormalized) ?? null;
    }
    return null;
  }, [flows, flowId, docPathNormalized]);

  const { triggerRun, setPosture, loading: actionBusy } = useFlowActions();
  const { runs, loading: runsLoading, error: runsError, refetch: refetchRuns } =
    useFlowRuns(isPersisted ? flowId : '', { pollMs: 1500, pollWhile: actionBusy });

  const fm = useMemo(() => document.frontmatter ?? {}, [document.frontmatter]);

  const triggers = useMemo(
    () => coerceTriggers(fm.triggers as unknown, flow?.triggers),
    [fm.triggers, flow?.triggers]
  );

  const policy = useMemo(() => {
    const p = fm.policy as Record<string, unknown> | undefined;
    return p && typeof p === 'object' ? p : undefined;
  }, [fm.policy]);

  // ---------------------------------------------------------------------------
  // Requirements check — fetch satisfaction state for fm.requires declarations.
  // Supported: 'git_source' (checks /api/git-sources count).
  // ---------------------------------------------------------------------------
  const fmRequires: string[] = useMemo(() => {
    const r = fm.requires;
    if (Array.isArray(r)) return r.map(String);
    return [];
  }, [fm.requires]);

  const [unmetRequirements, setUnmetRequirements] = useState<string[]>([]);
  useEffect(() => {
    if (!isPersisted || fmRequires.length === 0) {
      setUnmetRequirements([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const unmet: string[] = [];
      if (fmRequires.includes('git_source')) {
        try {
          const res = await import('@/lib/api').then(({ fetchWithAuth }) =>
            fetchWithAuth('/api/git-sources')
          );
          const data = await res.json() as { gitSources?: unknown[]; git_sources?: unknown[] };
          const sources = data.gitSources ?? data.git_sources ?? [];
          if (!Array.isArray(sources) || sources.length === 0) unmet.push('git_source');
        } catch { unmet.push('git_source'); }
      }
      if (!cancelled) setUnmetRequirements(unmet);
    })();
    return () => { cancelled = true; };
  }, [isPersisted, fmRequires]);

  const latestRunError = runs[0]?.error ?? null;
  const needsBasecampAuth = useMemo(
    () => flowNeedsBasecampAuth(fm, latestRunError),
    [fm, latestRunError],
  );
  const { authStatus: basecampAuth, isLoading: basecampAuthLoading } = useBasecampAuth();
  const basecampUnmet =
    needsBasecampAuth && !basecampAuthLoading && Boolean(basecampAuth) && !basecampAuth?.authenticated;
  const headerUnmet = useMemo(
    () => (basecampUnmet ? [...unmetRequirements, 'basecamp'] : unmetRequirements),
    [basecampUnmet, unmetRequirements],
  );
  const sawBasecampUnauth = useRef(false);
  useEffect(() => {
    if (!needsBasecampAuth) return;
    if (basecampAuth && !basecampAuth.authenticated) sawBasecampUnauth.current = true;
    if (basecampAuth?.authenticated && sawBasecampUnauth.current) {
      sawBasecampUnauth.current = false;
      toast.success('Basecamp connected. Click Run now to retry.');
    }
  }, [needsBasecampAuth, basecampAuth]);

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runStepIndex, setRunStepIndex] = useState<number | null>(null);
  const [definitionStep, setDefinitionStep] = useState<number | null>(null);
  const [pendingFields, setPendingFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId(null);
      return;
    }
    setSelectedRunId((prev) =>
      prev && runs.some((r) => r.id === prev) ? prev : runs[0]?.id ?? null
    );
  }, [runs]);

  const selectedRun =
    runs.find((r) => r.id === selectedRunId) ?? (runs.length > 0 ? runs[0] : null);

  const { events: runEvents, loading: eventsLoading } = useFlowRunEvents(
    isPersisted ? flowId : '',
    selectedRun?.id ?? null,
    {
      pollMs: 1500,
      pollWhile:
        actionBusy ||
        selectedRun?.status === 'running' ||
        selectedRun?.status === 'pending',
    }
  );

  const derivedSteps = useMemo(
    () => stepsFromEvents(runEvents, selectedRun?.status),
    [runEvents, selectedRun?.status],
  );

  useEffect(() => {
    setRunStepIndex(null);
  }, [selectedRun?.id]);

  useEffect(() => {
    setDefinitionStep(null);
    setPendingFields({});
  }, [flowId]);

  const definitionSteps = useMemo(
    () => (Array.isArray(fm.steps) ? (fm.steps as { id?: string; inputs?: Record<string, unknown> }[]) : []),
    [fm.steps],
  );
  const posture = flow ? flowPosture(flow) : 'disabled';

  const commitField = useCallback(async (index: number, field: string, value: string) => {
    const step = definitionSteps[index];
    const inputs = step?.inputs && typeof step.inputs === 'object' ? step.inputs : {};
    const saved = typeof inputs[field] === 'string' ? inputs[field] : '';
    const key = `${index}:${field}`;
    if (value === saved) {
      setPendingFields((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    if (posture === 'live') {
      setPendingFields((prev) => ({ ...prev, [key]: value }));
      return;
    }
    const steps = definitionSteps.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return { ...item, inputs: { ...(item.inputs || {}), [field]: value } };
    });
    try {
      await updateDocument(flowId, {
        frontmatter: { steps },
        saveEditHistory: true,
        changedBy: 'user',
        historyMetadata: { saveTrigger: 'manual' },
      });
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: flowId } }));
    } catch {
      toast.error('Could not save this field');
    }
  }, [definitionSteps, flowId, posture]);

  const approvePending = useCallback(async () => {
    const steps = definitionSteps.map((item, index) => {
      const inputs = { ...(item.inputs || {}) };
      for (const [key, value] of Object.entries(pendingFields)) {
        const split = key.indexOf(':');
        if (Number(key.slice(0, split)) !== index) continue;
        inputs[key.slice(split + 1)] = value;
      }
      return { ...item, inputs };
    });
    try {
      await updateDocument(flowId, {
        frontmatter: { steps },
        saveEditHistory: true,
        changedBy: 'user',
        historyMetadata: { saveTrigger: 'manual' },
      });
      if (posture === 'live') await setPosture(flowId, 'shadow');
      setPendingFields({});
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: flowId } }));
      void refetchFlowsList();
      toast.success(posture === 'live' ? 'Saved. This flow is now Shadow.' : 'Saved');
    } catch {
      toast.error('Could not approve this edit');
    }
  }, [definitionSteps, flowId, pendingFields, posture, refetchFlowsList, setPosture]);

  const triggerSummary = formatTriggerSummary(triggers);
  const nextHint = nextScheduleHint(triggers);
  const lastStarted = runs[0]?.started_at ? formatRelativeTime(runs[0].started_at) : '-';

  const enabledEffective = flow?.enabled ?? false;
  const paused = Boolean(flow?.paused);

  const [docTab, setDocTab] = useState<FlowDocTab>(() => readFlowTab(flowId));
  const [definitionView, setDefinitionView] = useState<'visual' | 'source'>('visual');
  const [fullWidth, setFullWidth] = useState(false);

  const selectDocTab = useCallback((tab: FlowDocTab) => {
    setDocTab(tab);
    rememberFlowTab(flowId, tab);
  }, [flowId]);

  const handleTriggerRun = useCallback(async () => {
    if (!isPersisted) return;
    try {
      let payload: Record<string, unknown> = {};
      if (needsManualTriggerPayload(fm, triggers)) {
        const collected = collectManualTriggerPayloadInteractive(fm, {}, triggers);
        if (collected.cancelled) return;
        payload = collected.payload;
      }
      selectDocTab('runs');
      await triggerRun(flowId, payload);
      toast.success('Run started');
      void refetchRuns();
      void refetchFlowsList();
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401) return;
      if (status === 409) {
        toast.error('A run is already in progress');
        void refetchRuns();
        return;
      }
      toast.error('Failed to trigger flow run');
    }
  }, [flowId, fm, isPersisted, refetchFlowsList, refetchRuns, selectDocTab, triggerRun, triggers]);

  const openRefine = useCallback(() => {
    window.dispatchEvent(new CustomEvent('flownotes:open-chat-prompt', {
      detail: {
        query: 'Refine this flow. Propose step changes and wait for approval before writing.',
        newThread: true,
        documentReference: {
          id: flowId,
          title: document.title || 'Flow',
          type: 'flow',
          path: docPath || flowId,
        },
      },
    }));
  }, [docPath, document.title, flowId]);

  useEffect(() => {
    const onRunNow = (event: Event) => {
      const detail = (event as CustomEvent<{ flowId?: string }>).detail;
      if (detail?.flowId !== flowId) return;
      if (!enabledEffective || paused) {
        toast.error(paused ? 'This flow is paused by the system' : 'Disabled flows cannot run');
        return;
      }
      void handleTriggerRun();
    };
    const onOpenTab = (event: Event) => {
      const detail = (event as CustomEvent<{ flowId?: string; tab?: FlowDocTab }>).detail;
      if (detail?.flowId !== flowId || !detail.tab) return;
      selectDocTab(detail.tab);
    };
    window.addEventListener(FLOW_RUN_NOW_EVENT, onRunNow);
    window.addEventListener(FLOW_OPEN_TAB_EVENT, onOpenTab);
    return () => {
      window.removeEventListener(FLOW_RUN_NOW_EVENT, onRunNow);
      window.removeEventListener(FLOW_OPEN_TAB_EVENT, onOpenTab);
    };
  }, [enabledEffective, flowId, handleTriggerRun, paused, selectDocTab]);

  const policyLine =
    policy && typeof policy.runs_per_hour === 'number'
      ? `Up to ${policy.runs_per_hour} runs per hour (frontmatter)`
      : flowsError
        ? 'Flow state could not sync (see API)'
        : 'Policy from frontmatter; caps enforced at runtime';

  const latestRun = runs[0] ?? null;
  const runInFlight = runs.some((r) => {
    const s = String(r.status || '').toLowerCase();
    return s === 'running' || s === 'pending' || s === 'queued';
  });

  const persistGate = (
    <div className="flex flex-col items-center justify-center h-full text-base text-muted-foreground/95 leading-relaxed px-6 text-center gap-3 max-w-md mx-auto">
      <Sparkles className="h-7 w-7 text-foreground/80 shrink-0" />
      <p className="text-foreground/90">
        Save this flow document first to load runs, triggers, and enable/disable from the flows
        API.
      </p>
    </div>
  );

  if (!isPersisted) {
    return persistGate;
  }

  return (
    <div
      className="h-full flex flex-col min-h-0 overflow-hidden bg-background"
      data-testid={testIds.flowDocumentRenderer.root}
    >
      <Tabs
        value={docTab}
        onValueChange={(value) => selectDocTab(value === 'runs' ? 'runs' : 'definition')}
        className="flex-1 flex flex-col min-h-0"
        data-testid={testIds.flowDocumentRenderer.tabs}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5">
          <TabsList className="h-8 w-auto gap-1 bg-transparent p-0">
            <TabsTrigger value="definition" className="h-8 rounded-md px-3 text-sm data-[state=active]:bg-muted data-[state=active]:shadow-none">
              Definition
            </TabsTrigger>
            <TabsTrigger value="runs" className="h-8 rounded-md px-3 text-sm data-[state=active]:bg-muted data-[state=active]:shadow-none">
              Runs
            </TabsTrigger>
          </TabsList>
          {headerUnmet.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-8 px-2 text-xs gap-1.5 text-orange-600 hover:bg-orange-500/10"
              onClick={() => {
                if (headerUnmet.includes('basecamp')) {
                  openBasecampAuthWindow();
                  return;
                }
                openSettings(
                  headerUnmet.includes('git_source') ? 'git-sources' : 'integrations'
                );
              }}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Missing: {headerUnmet.join(', ')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="h-8 px-2.5 text-sm"
            disabled={actionBusy || runInFlight || !isPersisted || !enabledEffective || paused}
            onClick={handleTriggerRun}
            data-testid={testIds.flowDocumentRenderer.runNow}
          >
            {actionBusy || runInFlight ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="mr-1 h-3.5 w-3.5" />
            )}
            {actionBusy || runInFlight ? 'Running' : 'Run now'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="h-8 px-2.5 text-sm"
            onClick={openRefine}
            data-testid={testIds.flowDocumentRenderer.refine}
          >
            <Sparkles className="mr-1 h-3.5 w-3.5" />
            Refine
          </Button>
        </div>

        <TabsContent value="definition" className="mt-0 flex-1 min-h-0 overflow-auto p-5 space-y-4">
          <div className={cn('mx-auto w-full space-y-4', fullWidth ? 'max-w-none' : 'max-w-[760px]')}>
          {paused && (
            <Alert className="border-orange-500/30 bg-orange-500/5">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertTitle>Paused by the system</AlertTitle>
              <AlertDescription>{flow?.paused_reason || 'This flow cannot run until it is unpaused.'}</AlertDescription>
            </Alert>
          )}
          {Object.keys(pendingFields).length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5">
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                This edit is parked. Approve saves it and moves the flow to Shadow.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setPendingFields({})}
              >
                Reject
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs"
                data-testid={testIds.flowDocumentRenderer.definitionApprove}
                onClick={() => void approvePending()}
              >
                Approve
              </Button>
            </div>
          )}
          {definitionView === 'visual' ? (
            <FlowDefinitionPipeline
              steps={definitionSteps}
              triggerSummary={triggerSummary}
              lastNext={runsLoading ? 'Loading...' : `Last ${lastStarted} / Next ${nextHint}`}
              selectedIndex={definitionStep}
              onSelect={setDefinitionStep}
              pendingFields={pendingFields}
              onCommitField={(index, field, value) => void commitField(index, field, value)}
            />
          ) : (
            <pre className="overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/20 p-3 font-mono text-xs">
              {document.content || ''}
            </pre>
          )}
          <p className="text-xs text-muted-foreground" data-testid={testIds.flowDocumentRenderer.scheduleNext}>
            {policyLine}
          </p>
          {basecampUnmet && (
            <Alert
              className="rounded-none border-orange-500/30 bg-orange-500/5"
              data-testid={testIds.flowDocumentRenderer.basecampAuthBanner}
            >
              <AlertTriangle className="h-[18px] w-[18px] text-orange-600" />
              <AlertTitle className="text-sm font-semibold text-foreground">
                Basecamp not connected
              </AlertTitle>
              <AlertDescription className="text-sm leading-relaxed text-foreground/85 mt-1">
                This flow needs your Basecamp token. Connecting does not rewrite a past failed run.
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  className="mt-2 h-7 px-2 text-xs gap-1.5 border-orange-500/30 text-orange-600 hover:bg-orange-500/10"
                  onClick={() => openBasecampAuthWindow()}
                  data-testid={testIds.flowDocumentRenderer.connectBasecamp}
                >
                  Connect Basecamp
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {runsLoading && (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-12 w-2/3" />
            </div>
          )}

          {!runsLoading && runsError && (
            <p className="text-sm text-red-500 font-medium leading-relaxed">
              Could not load runs: {runsError.message}
            </p>
          )}

          {!runsLoading && !runsError && !latestRun && (
            <p className="text-sm text-muted-foreground/95 leading-relaxed">
              No runs yet - enable this flow or click Run now.
            </p>
          )}

          {!runsLoading && !runsError && latestRun && (
            <div className="border border-border/80 rounded-md bg-muted/[0.08] dark:bg-muted/10 p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold text-foreground truncate">Latest run</span>
                <RunStatusBadge status={latestRun.status} />
              </div>
              <p className="text-sm text-foreground/90">
                Trigger type: <span className="font-medium">{latestRun.trigger_type}</span>
              </p>
              {latestRun.error && (
                <>
                  <p className="mt-2 min-w-0 break-words text-sm leading-relaxed text-red-500">{latestRun.error}</p>
                  {BASECAMP_AUTH_ERROR.test(latestRun.error) ? (
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      Past run. Connect Basecamp above if needed, then click Run now.
                    </p>
                  ) : (
                    (() => {
                      const action = getErrorAction(latestRun.error);
                      return action ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 h-7 px-2 text-xs gap-1.5 border-orange-500/30 text-orange-600 hover:bg-orange-500/10"
                          onClick={() => runErrorAction(action)}
                        >
                          <Settings className="h-3 w-3" />
                          {action.label}
                        </Button>
                      ) : null;
                    })()
                  )}
                </>
              )}
              <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground/95 flex-wrap">
                <span>Started {formatRelativeTime(latestRun.started_at)}</span>
                {latestRun.output_thread_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1.5 ml-auto"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent('open-inbox-thread-main', {
                          detail: { threadId: latestRun.output_thread_id },
                        }),
                      )
                    }
                  >
                    <Inbox className="h-3 w-3" />
                    View in inbox
                  </Button>
                )}
              </div>
            </div>
          )}
          </div>
        </TabsContent>

        <TabsContent
          value="runs"
          className="mt-0 flex-1 min-h-0 overflow-hidden grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)]"
        >
          <div className="border-r border-border overflow-y-auto min-h-[200px]">
            {runsLoading && (
              <div className="p-3 space-y-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            )}
            {!runsLoading && runsError && (
              <p className="text-sm text-red-500 font-medium leading-relaxed p-4">{runsError.message}</p>
            )}
            {!runsLoading && !runsError && runs.length === 0 && (
              <p className="text-sm text-muted-foreground/95 px-4 py-5 leading-relaxed">No runs yet.</p>
            )}
            {!runsLoading &&
              !runsError &&
              runs.map((run: FlowRun) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => setSelectedRunId(run.id)}
                  className={cn(
                    'w-full text-left px-3 py-3 border-b border-border border-l-[3px] transition-colors',
                    selectedRun?.id === run.id
                      ? 'bg-primary/5 border-l-primary'
                      : 'hover:bg-muted/40 border-l-transparent'
                  )}
                  data-testid={testIds.flowsManager.runRow(run.id)}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <RunStatusBadge status={run.status} />
                  </div>
                  <p className="text-xs font-medium text-foreground/80 tabular-nums">
                    {formatRelativeTime(run.started_at)}
                  </p>
                  <p className="text-xs text-muted-foreground/95 truncate mt-1 leading-snug">
                    {run.trigger_type}
                  </p>
                </button>
              ))}
          </div>
          <div className="p-5 overflow-y-auto min-h-[200px]">
            {!selectedRun && !runsLoading && (
              <p className="text-sm text-muted-foreground/95 leading-relaxed">Select a run.</p>
            )}
            {selectedRun && (
              <div className="space-y-3">
                <div
                  role="button"
                  tabIndex={0}
                  aria-pressed={runStepIndex === null}
                  data-testid={testIds.flowDocumentRenderer.runSummary}
                  onClick={() => setRunStepIndex(null)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setRunStepIndex(null);
                    }
                  }}
                  className={cn(
                    'min-w-0 rounded-md border p-4 text-left',
                    runStepIndex === null
                      ? 'border-primary bg-primary/5'
                      : 'border-border/80 bg-muted/[0.08] hover:bg-muted/20 dark:bg-muted/10',
                  )}
                >
                  <p className="text-sm font-semibold text-foreground mb-1.5">Run summary</p>
                  <p className="text-sm text-foreground/90 leading-relaxed">{selectedRun.trigger_type}</p>
                  {typeof selectedRun.trigger_payload?.file_path === 'string' && (
                    <div className="mt-2 flex items-start gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                      <span className="text-xs font-mono text-foreground/80 break-all leading-relaxed">
                        {selectedRun.trigger_payload.file_path as string}
                      </span>
                    </div>
                  )}
                  {selectedRun.error && (
                    <>
                      <p className="mt-2 min-w-0 break-words text-sm leading-relaxed text-red-500">{selectedRun.error}</p>
                      {BASECAMP_AUTH_ERROR.test(selectedRun.error) ? (
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                          Past run. Connect Basecamp on Overview if needed, then click Run now.
                        </p>
                      ) : (
                        (() => {
                          const action = getErrorAction(selectedRun.error);
                          return action ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 h-7 px-2 text-xs gap-1.5 border-orange-500/30 text-orange-600 hover:bg-orange-500/10"
                              onClick={(event) => {
                                event.stopPropagation();
                                runErrorAction(action);
                              }}
                            >
                              <Settings className="h-3 w-3" />
                              {action.label}
                            </Button>
                          ) : null;
                        })()
                      )}
                    </>
                  )}
                  <div className="mt-3 text-sm text-muted-foreground/95 flex items-center gap-4 flex-wrap">
                    <span>Started {formatRelativeTime(selectedRun.started_at)}</span>
                    {selectedRun.output_thread_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1.5 ml-auto"
                        onClick={(event) => {
                          event.stopPropagation();
                          window.dispatchEvent(
                            new CustomEvent('open-inbox-thread-main', {
                              detail: { threadId: selectedRun.output_thread_id },
                            }),
                          );
                        }}
                      >
                        <Inbox className="h-3 w-3" />
                        View in inbox
                      </Button>
                    )}
                  </div>
                </div>
                {eventsLoading && (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-32 w-full" />
                  </div>
                )}
                {!eventsLoading && (
                  <RunStepLog
                    steps={derivedSteps}
                    selectedIndex={runStepIndex}
                    onSelect={setRunStepIndex}
                  />
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <div
        className="@container flex h-10 shrink-0 items-center gap-2 border-t border-border bg-muted/30 px-2 text-xs @[620px]:gap-3 @[620px]:px-4"
        data-testid={testIds.flowDocumentRenderer.viewMode}
      >
        {docTab === 'definition' && (
          <div className="inline-flex shrink-0 items-center rounded-md border border-border p-0.5" role="tablist" aria-label="Definition view">
            <button
              type="button"
              role="tab"
              aria-selected={definitionView === 'visual'}
              aria-label="Visual"
              className={cn(
                'flex items-center justify-center gap-0 rounded-sm px-1.5 py-1 text-xs @[620px]:gap-1.5 @[620px]:px-2',
                definitionView === 'visual' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              )}
              onClick={() => setDefinitionView('visual')}
            >
              <Eye className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden @[620px]:inline">Visual</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={definitionView === 'source'}
              aria-label="Source"
              className={cn(
                'flex items-center justify-center gap-0 rounded-sm px-1.5 py-1 text-xs @[620px]:gap-1.5 @[620px]:px-2',
                definitionView === 'source' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              )}
              onClick={() => setDefinitionView('source')}
            >
              <Code className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden @[620px]:inline">Source</span>
            </button>
          </div>
        )}
        <button
          type="button"
          className="flex shrink-0 items-center justify-center gap-0 rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground @[620px]:gap-1.5 @[620px]:px-2"
          title={fullWidth ? 'Switch to reading column (760px)' : 'Switch to full width'}
          aria-label={fullWidth ? 'Full' : 'Column'}
          onClick={() => setFullWidth((value) => !value)}
          data-testid={testIds.documentContentStatusBar.pageWidthToggle}
        >
          {fullWidth ? <StretchHorizontal className="h-3.5 w-3.5" aria-hidden /> : <AlignCenter className="h-3.5 w-3.5" aria-hidden />}
          <span className="hidden @[620px]:inline">{fullWidth ? 'Full' : 'Column'}</span>
        </button>
      </div>
    </div>
  );
}
